import type { UserGroup } from '@gmn-portal/backend';
import { zodResolver } from '@hookform/resolvers/zod';
import { Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PayloadApiError, payloadClient, startMicrosoftLogin } from '@/core/api';
import type { Locale } from '@/core/i18n/i18n';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator,
} from '@/shared/components/ui';
import z from '@/shared/utils/zod-translations';

import { MicrosoftIcon } from '../components/microsoft-icon';

interface InviteData {
  firstName: string;
  lastName: string;
  customer?: string;
  email: string;
  language: Locale;
  userGroup?: string | UserGroup;
}

export function AcceptInvite() {
  const { t, change, locale } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');

  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [inviteAccepted, setInviteAccepted] = useState<boolean>(false);
  const [tokenInvalidOrExpired, setTokenInvalidOrExpired] = useState<boolean>(false);

  const formSchema = z
    .object({
      password: z.string().min(8).max(256),
      passwordConfirmation: z.string().min(8).max(256),
    })
    .refine((data) => data.password === data.passwordConfirmation, {
      message: t('auth.invalidPasswordConfirmation'),
      path: ['passwordConfirmation'],
    });

  type AcceptInviteForm = z.infer<typeof formSchema>;

  const form = useForm<AcceptInviteForm, unknown, AcceptInviteForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      passwordConfirmation: '',
    },
  });

  useEffect(() => {
    if (!token) {
      navigate('/auth/login');
      return;
    }

    const fetchInviteData = async () => {
      try {
        const response = await payloadClient.customRequest<InviteData>({
          subpath: '/invites/get-invite-data',
          method: 'POST',
          data: { token },
        });
        setInviteData(response);
        if (!localStorage.getItem('selectedLocale')) {
          localStorage.setItem('selectedLocale', response.language);
          change(response.language);
        }
      } catch (_error) {
        setTokenInvalidOrExpired(true);
      }
    };

    fetchInviteData();
  }, [token, navigate, change]);

  async function onSubmitPassword(values: AcceptInviteForm) {
    try {
      setIsLoading(true);
      await payloadClient.customRequest({
        subpath: '/invites/accept-invite',
        method: 'POST',
        data: { token, password: values.password, language: locale },
      });
      setInviteAccepted(true);
    } catch (error) {
      if (error instanceof PayloadApiError && error.statusCode === 401) {
        setTokenInvalidOrExpired(true);
      } else {
        form.setError('root', { message: t('errors.generalError') });
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleMicrosoftLogin() {
    if (!token) return;

    try {
      setIsLoading(true);

      const group =
        inviteData?.userGroup && typeof inviteData.userGroup === 'object'
          ? inviteData.userGroup
          : null;
      const mode = group?.domain === 'gmn' ? 'gmn' : 'customer';

      await startMicrosoftLogin({
        inviteToken: token,
        mode,
        redirectPath: '/', // After successful acceptance, go to home
      });
    } catch (_error) {
      // Error is already handled by the API layer
      form.setError('root', { message: t('errors.generalError') });
      setIsLoading(false);
    }
  }

  if (tokenInvalidOrExpired) {
    return (
      <div className="space-y-4 text-center">
        <h1 className="text-3xl font-bold">{t('auth.invalidOrExpiredInvite')}</h1>
        <p>{t('auth.pleaseContactAdmin')}</p>
        <Link to="/auth/login" className="block text-sm underline">
          {t('auth.backToLogin')}
        </Link>
      </div>
    );
  }

  if (!inviteData) {
    return <div>{t('common.loading')}</div>;
  }

  const loginUrl = `/auth/login?email=${encodeURIComponent(inviteData.email)}`;
  const inviteGroup =
    inviteData.userGroup && typeof inviteData.userGroup === 'object' ? inviteData.userGroup : null;
  const isGmnInvite = inviteGroup?.domain === 'gmn';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">{t('auth.acceptInvite')}</h1>
        {!inviteAccepted && (
          <p
            className="text-muted-foreground text-balance"
            /* biome-ignore lint/security/noDangerouslySetInnerHtml: localized string contains markup */
            dangerouslySetInnerHTML={{
              __html: inviteData.customer
                ? t('auth.welcomeMessage', {
                    name: `${inviteData.firstName} ${inviteData.lastName}`,
                    customer: inviteData.customer,
                  })
                : t('auth.welcomeMessageNoCustomer', {
                    name: `${inviteData.firstName} ${inviteData.lastName}`,
                  }),
            }}
          />
        )}
      </div>

      {!inviteAccepted && (
        <div className="space-y-6">
          {/* Microsoft Login Card */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <MicrosoftIcon className="h-5 w-5" />
                {t('auth.continueWithMicrosoft')}
              </CardTitle>
              <CardDescription>{t('auth.microsoftInviteDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* GMN-specific notice */}
              {isGmnInvite && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>{t('auth.gmnMicrosoftRequired')}</AlertDescription>
                </Alert>
              )}
              <Button
                variant={isGmnInvite ? 'default' : 'outline'}
                className="w-full"
                onClick={handleMicrosoftLogin}
                disabled={isLoading}
                size="lg"
              >
                <MicrosoftIcon className="mr-2 h-5 w-5" />
                {t('auth.continueWithMicrosoft')}
              </Button>
            </CardContent>
          </Card>

          {/* Separator */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-3 text-muted-foreground">
                {t('auth.orSetPassword')}
              </span>
            </div>
          </div>

          {/* Password Form Card */}
          <Card className="border-dashed">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">{t('auth.setPassword')}</CardTitle>
              <CardDescription>{t('auth.setPasswordDescription')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmitPassword)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('auth.newPassword')}</FormLabel>
                        <FormControl>
                          <Input type="password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="passwordConfirmation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('auth.passwordConfirmation')}</FormLabel>
                        <FormControl>
                          <Input type="password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormMessage>{form.formState.errors.root?.message}</FormMessage>
                  <Button type="submit" variant="outline" className="w-full" disabled={isLoading}>
                    {isLoading ? t('auth.acceptingInvite') : t('auth.acceptInvite')}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      )}

      {inviteAccepted && (
        <Card>
          <CardContent className="pt-6 text-center space-y-4">
            <p className="text-muted-foreground">{t('auth.inviteAccepted')}</p>
            <Button asChild>
              <Link to={loginUrl}>{t('auth.goToLogin')}</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="text-center">
        <Link className="text-sm text-muted-foreground hover:underline" to={loginUrl}>
          {t('auth.alreadyHaveAccount')}
        </Link>
      </div>
    </div>
  );
}
